import {
  Activity,
  FileText,
  Shield,
  UserCheck,
  Workflow,
  type LucideIcon,
} from "lucide-react";
import type { AuditEventItem } from "@/hooks/useAuditEvents";

/** Minimal shape formatAuditTitle needs from react-i18next's TFunction. */
export type TranslateFn = (
  key: string,
  options?: Record<string, unknown>,
) => string;

/**
 * Human-readable audit event title per the spec-by-example table (AC's
 * decision -> artifact -> commit narrative). Purely presentational —
 * consumed by AuditTimeline (SD1).
 *
 * phase_transition: there is no `phase_next` column in
 * GovernanceAuditEventRow (only `subject_id`, which carries the single
 * phase that completed) — the "design -> implement" arrow form from the
 * story's spec-by-example is aspirational and not derivable from the
 * current schema. This renders the honest single-phase form instead
 * (design doc's Critical Verified Finding / SD1), never fabricating a
 * transition the data doesn't carry.
 */
function decisionVerb(decision: string, t: TranslateFn): string {
  const key = `audit.decisions.${decision}`;
  const translated = t(key);
  return translated === key ? decision : translated;
}

export function formatAuditTitle(
  event: AuditEventItem,
  t: TranslateFn,
): string {
  const decision = event.decision
    ? decisionVerb(event.decision, t)
    : t("audit.unspecifiedDecision");

  switch (event.event_kind) {
    case "gate_decision": {
      const ref = event.work_item_ref ?? t("audit.unknownRef");
      return t("audit.titles.gateDecision", {
        subject: event.subject_id,
        decision,
        ref,
      });
    }
    case "hitl_decision": {
      const target = event.work_item_ref ?? event.subject_id;
      return t("audit.titles.hitlDecision", { decision, target });
    }
    case "phase_transition": {
      const ref = event.work_item_ref ?? t("audit.unknownRef");
      return t("audit.titles.phaseTransition", {
        subject: event.subject_id,
        ref,
      });
    }
    case "artifact_created": {
      const ref = event.work_item_ref ?? t("audit.unknownRef");
      return t("audit.titles.artifactCreated", { ref });
    }
    default:
      return t("audit.titles.generic", {
        kind: event.event_kind,
        ref: event.work_item_ref ?? event.subject_id,
      });
  }
}

// ---------------------------------------------------------------------------
// Decision badge color (SD1)
// ---------------------------------------------------------------------------

export type DecisionColor = "green" | "red" | "amber" | "neutral";

const DECISION_COLORS: Record<string, DecisionColor> = {
  pass: "green",
  approve: "green",
  auto_approve: "green",
  fail: "red",
  reject: "red",
  crash: "red",
  skip: "amber",
  revise: "amber",
};

export function decisionColor(decision: string | null): DecisionColor {
  if (decision === null) return "neutral";
  return DECISION_COLORS[decision] ?? "neutral";
}

export const DECISION_BADGE_CLASSES: Record<DecisionColor, string> = {
  green: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  red: "bg-red-500/10 text-red-400 border-red-500/20",
  amber: "bg-amber-500/10 text-amber-400 border-amber-500/20",
  neutral: "bg-muted text-muted-foreground border-border",
};

// ---------------------------------------------------------------------------
// Kind icon (SD1)
// ---------------------------------------------------------------------------

const KIND_ICONS: Record<string, LucideIcon> = {
  gate_decision: Shield,
  hitl_decision: UserCheck,
  phase_transition: Workflow,
  artifact_created: FileText,
};

export function auditKindIcon(kind: string): LucideIcon {
  return KIND_ICONS[kind] ?? Activity;
}
